// resources/js/Pages/Formula/utils/schemaUpdates.js
// DND: FIXED - Completely rewritten relocateNode to be robust and handle all cases.

import { v4 as uuid } from 'uuid';

export function traverseAndUpdate(node, updateFn) {
  if (!node) return null;
  let updatedNode = updateFn(node);

  if (updatedNode && updatedNode.children) {
    const updatedChildren = updatedNode.children.map(child => traverseAndUpdate(child, updateFn)).filter(Boolean);
    updatedNode = { ...updatedNode, children: updatedChildren };
  }

  return updatedNode;
}

// Helper to find a node and its parent. Crucially, it returns the objects themselves.
const findNodeAndParent = (startNode, nodeId, parent = null) => {
  if (startNode.id === nodeId) {
    return { node: startNode, parent };
  }
  if (startNode.children) {
    for (const child of startNode.children) {
      const found = findNodeAndParent(child, nodeId, startNode);
      if (found) return found;
    }
  }
  return null;
};

export function relocateNode(schema, { activeId, overId, isBelow }) {
  // 1. Find all necessary nodes and parents from the original, unmodified schema.
  const activeResult = findNodeAndParent(schema, activeId);
  const overResult = findNodeAndParent(schema, overId);

  if (!activeResult || !overResult) {
    console.error("DND Error: Could not find active or over node.", { activeId, overId });
    return schema;
  }

  const { node: activeNode, parent: sourceParent } = activeResult;
  const { node: overNode, parent: overParent } = overResult;

  if (!sourceParent) {
    console.error("DND Error: Dragged item has no parent (cannot drag root).", { activeNode });
    return schema;
  }

  // 2. Determine the true destination container and the index for insertion.
  let destinationParent;
  let destinationIndex;

  const isDroppingInContainer = overNode.children !== undefined && overNode.id === overId;

  if (isDroppingInContainer) {
    // Case A: Dropping directly ON a droppable container (like an empty Section or the Root).
    destinationParent = overNode;
    destinationIndex = destinationParent.children.length; // Add to the end.
  } else {
    // Case B: Dropping ON another sortable item (reordering).
    // The destination is the parent of the item we're hovering over.
    destinationParent = overParent;
    if (!destinationParent) {
      console.error("DND Error: Over item has no parent.", { overNode });
      return schema;
    }
    // Find the index of the item we're hovering over and adjust for insertion position.
    const overIndex = destinationParent.children.findIndex(child => child.id === overId);
    destinationIndex = isBelow ? overIndex + 1 : overIndex;
  }

  // 3. Create the new schema state immutably.

  // First, create a version of the schema with the active node removed from its original location.
  const schemaWithNodeRemoved = traverseAndUpdate(schema, (node) => {
    if (node.id === sourceParent.id) {
      return {
        ...node,
        children: node.children.filter(child => child.id !== activeId),
      };
    }
    return node;
  });

  // Then, take that new schema and insert the active node into its new destination.
  const finalSchema = traverseAndUpdate(schemaWithNodeRemoved, (node) => {
    if (node.id === destinationParent.id) {
      const newChildren = [...(node.children || [])];
      newChildren.splice(destinationIndex, 0, activeNode);

      // Enforce: row_detail must always be the last child of a table array
      const rowDetailIdx = newChildren.findIndex(c => c.type === 'row_detail');
      if (rowDetailIdx !== -1 && rowDetailIdx !== newChildren.length - 1) {
        const [rowDetailNode] = newChildren.splice(rowDetailIdx, 1);
        newChildren.push(rowDetailNode);
      }

      return { ...node, children: newChildren };
    }
    return node;
  });

  return finalSchema;
}

// ✅ NEW: Generic function to add any pre-configured element
export function createElementAtPosition(schema, parentId, position, elementTemplate) {
  const newElement = {
    id: uuid(),
    ...elementTemplate // Spread the template to pre-fill properties
  };

  const updatedSchema = traverseAndUpdate(schema, (node) => {
    if (node.id === parentId) {
      const children = [...(node.children || [])];
      children.splice(position, 0, newElement);
      return { ...node, children };
    }
    return node;
  });

  return { schema: updatedSchema, newElementId: newElement.id };
}

export function updateNodeType(schema, nodeId, newTypeTemplate) {
  // Get the original node to check its current type
  let originalNode = null;
  traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      originalNode = node;
    }
    return node;
  });

  // If the type isn't actually changing, do nothing.
  if (originalNode && originalNode.type === newTypeTemplate.type) {
    return schema;
  }

  // A clear confirmation dialog about the structural change
  const confirmationMessage = `You are changing this element to a "${newTypeTemplate.key}".\n\nThis will change its type and may reset some properties. Continue?`;

  // If the user cancels, return the original schema to prevent the error.
  if (!window.confirm(confirmationMessage)) {
    return schema; // This is the critical early return
  }

  // If the user confirms, proceed with updating the schema.
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      // Logic to convert a field to an AI Questions array
      if (newTypeTemplate.type === 'array' && newTypeTemplate.renderAs === 'llm_questions') {
        const { valueType, ...nodeWithoutValueType } = node;
        return {
          ...nodeWithoutValueType,
          ...newTypeTemplate,
          id: node.id,
          key: node.key || newTypeTemplate.key,
          instruction: newTypeTemplate.instruction
        };
      }

      // Logic for regular field type updates (e.g., string to score)
      return {
        ...node,
        valueType: newTypeTemplate.valueType,
        instruction: newTypeTemplate.instruction
      };
    }
    return node;
  });
}

// resources/js/Pages/Formula/utils/schemaUpdates.js
// ... (all other functions remain the same) ...

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addListAtPosition(schema, parentId, position, display = 'ul') {
  const arrayTemplate = {
    type: 'array',
    key: 'new_array',
    display,
    instruction: '', // Required for arrays
    children: []
  };

  const result = createElementAtPosition(schema, parentId, position, arrayTemplate);
  return result.schema;
}

// ... (rest of the file remains the same) ...
// ✅ Update node properties
export function updateNodeInstruction(schema, nodeId, newInstruction) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, instruction: newInstruction };
    }
    return node;
  });
}

export function updateNodeKey(schema, nodeId, newKey) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, key: newKey };
    }
    return node;
  });
}

export function updateNodeDisplay(schema, nodeId, newDisplay) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, display: newDisplay };
    }
    return node;
  });
}

// ✅ NEW: Convert field to array
export function convertFieldToArray(schema, nodeId, displayType) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId && node.type === 'field') {
      return {
        ...node,
        type: 'array',
        display: displayType,
        instruction: '', // User must fill this in
        children: [],
        // Remove field-specific properties

        valueType: undefined
      };
    }
    return node;
  });
}

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addFieldToNode(schema, parentId, fieldType = 'field') {
  const fieldTemplate = {
    type: 'field',
    key: 'new_field',
    instruction: '',
    valueType: 'string'
  };

  return traverseAndUpdate(schema, (node) => {
    if (node.id === parentId) {
      const newElement = {
        id: uuid(),
        ...fieldTemplate
      };
      return {
        ...node,
        children: [...(node.children || []), newElement]
      };
    }
    return node;
  });
}

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addFieldAtPosition(schema, parentId, position) {
  const fieldTemplate = {
    type: 'field',
    key: 'new_field',
    instruction: '',
    valueType: 'string'
  };

  const result = createElementAtPosition(schema, parentId, position, fieldTemplate);
  return { schema: result.schema, newFieldId: result.newElementId };
}

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addTextBlockAtPosition(schema, parentId, position) {
  const textBlockTemplate = {
    type: 'text-block',
    content: 'Add your text here...'
  };

  const result = createElementAtPosition(schema, parentId, position, textBlockTemplate);
  return result.schema;
}

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addSectionAtPosition(schema, parentId, position) {
  const sectionTemplate = {
    type: 'section',
    title: 'New Section',
    children: []
  };

  const result = createElementAtPosition(schema, parentId, position, sectionTemplate);
  return result.schema;
}

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addDividerAtPosition(schema, parentId, position) {
  const dividerTemplate = {
    type: 'divider'
  };

  const result = createElementAtPosition(schema, parentId, position, dividerTemplate);
  return result.schema;
}

// ✅ DEPRECATED: Kept for backward compatibility, use createElementAtPosition instead
export function addGraphAtPosition(schema, parentId, position) {
  const graphTemplate = {
    type: 'graph',
    key: 'new_graph',
    instruction: 'Describe the desired graph (e.g., a timeline of events)'
  };

  const result = createElementAtPosition(schema, parentId, position, graphTemplate);
  return result.schema;
}

// ✅ Delete node
export function deleteNode(schema, nodeId) {
  return traverseAndUpdate(schema, (node) => {
    if (node.children) {
      return {
        ...node,
        children: node.children.filter(child => child.id !== nodeId)
      };
    }
    return node;
  });
}

// ✅ Move node up/down within parent
export function moveNode(schema, nodeId, direction) {
  return traverseAndUpdate(schema, (node) => {
    if (node.children) {
      const children = [...node.children];
      const index = children.findIndex(child => child.id === nodeId);

      if (index !== -1) {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < children.length) {

          [children[index], children[newIndex]] = [children[newIndex], children[index]];
        }
      }

      return { ...node, children };
    }
    return node;
  });
}

// ✅ DND: This function has been moved to the top of the file
export function relocateNodeOld(schema, { activeId, overId }) {
  // 1. Find all necessary nodes and parents from the original, unmodified schema.
  const activeResult = findNodeAndParent(schema, activeId);
  const overResult = findNodeAndParent(schema, overId);

  if (!activeResult || !overResult) {
    console.error("DND Error: Could not find active or over node.", { activeId, overId });
    return schema;
  }

  const { node: activeNode, parent: sourceParent } = activeResult;
  const { node: overNode, parent: overParent } = overResult;

  if (!sourceParent) {
    console.error("DND Error: Dragged item has no parent (cannot drag root).", { activeNode });
    return schema;
  }

  // 2. Determine the true destination container and the index for insertion.
  let destinationParent;
  let destinationIndex;

  // Case A: Dropping directly ON a droppable container (like a Section or an empty Root).
  // `overNode.children` checks if it's a container.
  if (overNode.children && overNode.id === overId) {
    destinationParent = overNode;
    // Place at the end of the container.
    destinationIndex = destinationParent.children.length;
  }
  // Case B: Dropping ON another sortable item (reordering).
  // The destination is the parent of the item we're hovering over.
  else {
    destinationParent = overParent;
    if (!destinationParent) {
      console.error("DND Error: Over item has no parent.", { overNode });
      return schema;
    }
    // Find the index of the item we're hovering over to insert before it.
    destinationIndex = destinationParent.children.findIndex(child => child.id === overId);
  }

  // 3. Create the new schema state immutably.

  // First, create a version of the schema with the active node removed from its original location.
  const schemaWithNodeRemoved = traverseAndUpdate(schema, (node) => {
    if (node.id === sourceParent.id) {
      return {
        ...node,
        children: node.children.filter(child => child.id !== activeId),
      };
    }
    return node;
  });

  // Then, take that new schema and insert the active node into its new destination.
  const finalSchema = traverseAndUpdate(schemaWithNodeRemoved, (node) => {
    if (node.id === destinationParent.id) {
      const newChildren = [...(node.children || [])];
      newChildren.splice(destinationIndex, 0, activeNode);
      return { ...node, children: newChildren };
    }
    return node;
  });

  return finalSchema;
}

// ✅ NEW: Validate schema structure
export function validateSchema(schema) {
  const errors = [];
  function validate(node, path = '') {
    if (!node || typeof node !== 'object') {
      errors.push(`${path}: Invalid node structure`);
      return;
    }

    if (!node.id || !node.key || !node.type) {
      errors.push(`${path}: Missing required properties (id, key, type)`);
    }

    if (node.type === 'array') {
      if (!node.instruction) {
        errors.push(`${path}: Array nodes require an instruction (what should each item represent?)`);
      }
      if (!node.display || !['ul', 'table', 'cards', 'tabs'].includes(node.display)) {
        errors.push(`${path}: Array nodes require a valid display type`);
      }

      // Validate row_detail constraints in tables
      if (node.display === 'table' && node.children) {
        const rowDetailChildren = node.children.filter(c => c.type === 'row_detail');
        if (rowDetailChildren.length > 1) {
          errors.push(`${path}: Table can have at most one row_detail`);
        }
        if (rowDetailChildren.length === 1) {
          const lastChild = node.children[node.children.length - 1];
          if (lastChild.type !== 'row_detail') {
            errors.push(`${path}: row_detail must be the last child of a table`);
          }
        }
      }
    }

    if (node.type === 'field') {
      if (node.children && node.children.length > 0) {
        errors.push(`${path}: Field nodes cannot have children`);
      }
    }

    // ✅ NEW: Validate UI elements
    if (node.type === 'text-block') {
      // Text blocks can have content but it's not required
      if (node.children && node.children.length > 0) {
        errors.push(`${path}: Text blocks cannot have children`);
      }
    }

    if (node.type === 'section') {
      if (!node.title) {
        errors.push(`${path}: Section nodes should have a title`);
      }
      // Sections can have children
    }

    if (node.type === 'divider') {
      if (node.children && node.children.length > 0) {
        errors.push(`${path}: Divider nodes cannot have children`);
      }
    }

    if (node.type === 'row_detail') {
      if (node.children) {
        node.children.forEach((child, i) => validate(child, `${path}.children[${i}]`));
      }
      return;
    }

    if (node.children) {
      node.children.forEach((child, index) => {
        validate(child, `${path}.children[${index}]`);
      });
    }
  }

  validate(schema, 'root');
  return errors;
}

// ✅ NEW: Migrate legacy schemas to new format
export function migrateLegacySchema(schema) {
  return traverseAndUpdate(schema, (node) => {
    // Convert old 'list' type to 'array'
    if (node.type === 'list') {
      return {
        ...node,
        type: 'array',
        display: node.display || 'ul'
      };
    }

    // Ensure root has correct type
    if (node.key ===
      'Root' && !node.type) {
      return {
        ...node,
        type: 'root'
      };
    }

    return node;
  });
}

// ✅ NEW: Create new schema from scratch
export function createNewSchema() {
  return {
    id: uuid(),
    type: 'root',
    key: 'Root',
    children: []
  };
}

// ✅ NEW: Schema statistics (for debugging)
export function getSchemaStats(schema) {
  let fieldCount = 0;
  let arrayCount = 0;
  let uiElementCount = 0; // ✅ NEW
  let maxDepth = 0;
  function traverse(node, depth = 0) {
    maxDepth = Math.max(maxDepth, depth);

    if (node.type === 'field') fieldCount++;
    if (node.type === 'array') arrayCount++;
    if (['text-block', 'section', 'divider'].includes(node.type)) uiElementCount++;
    // ✅ NEW

    if (node.children) {
      node.children.forEach(child => traverse(child, depth + 1));
    }
  }

  traverse(schema);

  return {
    fieldCount,
    arrayCount,
    uiElementCount, // ✅ NEW
    maxDepth,
    totalNodes: fieldCount + arrayCount + uiElementCount + 1 // +1 for root
  };
}

// ✅ NEW: Filter schema for LLM (remove UI elements, keep only data)
export function filterSchemaForLLM(schema) {
  // Static UI element types that should be filtered out
  const uiElementTypes = ['text-block', 'section', 'divider'];
  function filterNode(node) {
    if (!node) return null;
    // Skip UI elements entirely
    if (uiElementTypes.includes(node.type)) {
      return null;
    }

    // For container nodes (section, root), process their children
    if (node.children) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter(Boolean) // Remove null values
        .flat();
      // Flatten arrays from unwrapped sections

      // For section containers, unwrap them (return just their children)
      if (node.type === 'section') {
        return filteredChildren;
      }

      // For other containers (root, array), keep the container but with filtered children
      return {
        ...node,
        children: filteredChildren
      };
    }

    // Keep data nodes (field, array) as-is
    return node;
  }

  const filtered = filterNode(schema);

  // Handle the case where filtering might return an array (from unwrapped sections)
  if (Array.isArray(filtered)) {
    return {
      ...schema,
      children: filtered
    };
  }

  return filtered;
}

// ✅ NEW: Validate that schema has actual data content
export function validateSchemaHasData(schema) {
  const stats = getSchemaStats(schema);
  if (stats.fieldCount === 0 && stats.arrayCount === 0) {
    return {
      isValid: false,
      message: 'Schema must contain at least one field or array for the AI to generate data'
    };
  }

  return { isValid: true };
}

// ✅ NEW: Update UI element properties
export function updateTextBlockContent(schema, nodeId, newContent) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId && node.type === 'text-block') {
      return { ...node, content: newContent };
    }
    return node;
  });
}

export function updateSectionTitle(schema, nodeId, newTitle) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId && node.type === 'section') {
      return { ...node, title: newTitle };
    }
    return node;
  });
}

// ✅ NEW: Clean HTML content for safe storage
export function cleanHtmlContent(htmlContent) {
  if (!htmlContent) return '';

  // Basic HTML sanitization - remove script tags, etc.
  // In production, you'd want to use a proper HTML sanitizer like DOMPurify
  return htmlContent
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');
}

// ✅ NEW: Convert plain text to HTML for TinyMCE
export function textToHtml(plainText) {
  if (!plainText) return '';

  // Convert line breaks to paragraphs
  const paragraphs = plainText
    .split('\n\n')
    .filter(p => p.trim())
    .map(p => `<p>${p.trim().replace(/\n/g, '<br>')}</p>`);

  return paragraphs.join('');
}

// ✅ NEW: Convert HTML to plain text for fallback display
export function htmlToText(htmlContent) {
  if (!htmlContent) return '';

  // Remove HTML tags and convert common entities
  return htmlContent
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

// ✅ NEW: Migrate plain text content to HTML for existing schemas
export function migrateContentToHtml(schema) {
  return traverseAndUpdate(schema, (node) => {
    // Convert text block content to HTML if it's plain text
    if (node.type === 'text-block' && node.content) {
      // Check if content is already HTML (contains tags)
      const hasHtmlTags = /<[^>]*>/g.test(node.content);

      if (!hasHtmlTags) {
        return {
          ...node,
          content: textToHtml(node.content)
        };
      }
    }

    // Convert field instructions to HTML if they're plain text
    if (node.type === 'field' && node.instruction) {
      const hasHtmlTags = /<[^>]*>/g.test(node.instruction);

      if (!hasHtmlTags) {
        return {
          ...node,
          instruction: textToHtml(node.instruction)
        };
      }
    }

    // Convert array instructions to HTML if they're plain text
    if (node.type === 'array' && node.instruction) {
      const hasHtmlTags = /<[^>]*>/g.test(node.instruction);

      if (!hasHtmlTags) {
        return {
          ...node,
          instruction: textToHtml(node.instruction)
        };
      }
    }

    return node;
  });
}

// ✅ NEW: Move a node to a specific position within a parent (for insertion point drops)
export function moveNodeToPosition(schema, { activeId, parentId, position }) {
  // 1. Find the active node and its current parent
  const activeResult = findNodeAndParent(schema, activeId);
  if (!activeResult || !activeResult.parent) {
    console.error("DND Error: Could not find active node or it has no parent.", { activeId });
    return schema;
  }

  const { node: activeNode, parent: sourceParent } = activeResult;

  // 2. Find the target parent
  const targetParentResult = findNodeAndParent(schema, parentId);
  if (!targetParentResult) {
    console.error("DND Error: Could not find target parent.", { parentId });
    return schema;
  }

  const { node: targetParent } = targetParentResult;

  // 3. Remove the active node from its current location
  const schemaWithNodeRemoved = traverseAndUpdate(schema, (node) => {
    if (node.id === sourceParent.id) {
      return {
        ...node,
        children: node.children.filter(child => child.id !== activeId),
      };
    }
    return node;
  });

  // 4. Insert the active node at the specified position in the target parent
  const finalSchema = traverseAndUpdate(schemaWithNodeRemoved, (node) => {
    if (node.id === parentId) {
      const newChildren = [...(node.children || [])];
      // Ensure position is within bounds
      const insertPosition = Math.min(position, newChildren.length);
      newChildren.splice(insertPosition, 0, activeNode);
      return { ...node, children: newChildren };
    }
    return node;
  });

  return finalSchema;
}

// ✅ NEW: Update a node's renderAs property
export function updateNodeRenderAs(schema, nodeId, newRenderAs) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, renderAs: newRenderAs };
    }
    return node;
  });
}

// ✅ NEW: Update a node's chartType property
export function updateNodeChartType(schema, nodeId, newChartType) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, chartType: newChartType };
    }
    return node;
  });
}

// ✅ NEW: Update a node's valueType property
export function updateNodeValueType(schema, nodeId, newValueType) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, valueType: newValueType };
    }
    return node;
  });
}

// ✅ NEW: Update a node's collapsible property
export function updateNodeCollapsible(schema, nodeId, isCollapsible) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, collapsible: isCollapsible };
    }
    return node;
  });
}

// ✅ NEW: Update a node's showTitle property
export function updateNodeShowTitle(schema, nodeId, showTitle) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, showTitle: showTitle };
    }
    return node;
  });
}

// ✅ NEW: Update a node's isStacked property for tables
export function updateNodeStacked(schema, nodeId, isStacked) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, isStacked: isStacked };
    }
    return node;
  });
}

// ✅ NEW: Update a node's cardWidth property for card arrays
export function updateNodeCardWidth(schema, nodeId, cardWidth) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, cardWidth: cardWidth };
    }
    return node;
  });
}

// ✅ NEW: Update a node's layoutColumns property for grid layouts
export function updateNodeLayoutColumns(schema, nodeId, columnCount) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      // Ensure columnCount is a number, default to 1
      const columns = parseInt(columnCount, 10) || 1;
      return { ...node, layoutColumns: columns };
    }
    return node;
  });
}

export function updateNodeProperty(schema, nodeId, property, value) {
  return traverseAndUpdate(schema, (node) => {
    if (node.id === nodeId) {
      return { ...node, [property]: value };
    }
    return node;
  });
}